import React, { createContext, useCallback, useContext, useEffect, useState } from 'react';
import { featureFlagsAPI } from '../api/featureFlags';

/**
 * FeatureFlagContext — fetches the public flag map at app boot and exposes
 *   useFeatureFlag('rides')       -> boolean
 *   useFeatureFlags()             -> { flags, refresh, isEnabled, loaded }
 *
 * Per-key boot defaults (LOADING_DEFAULTS) prevent the "rides flash" bug:
 * gated verticals (`rides`, `food`) default to FALSE until the first
 * /api/feature-flags response arrives, so disabled features never appear
 * in the UI even for the first render. Cosmetic / always-on verticals
 * default to TRUE so the homepage isn't blank during the fetch.
 *
 * Unknown keys default to TRUE (fail-open) so a brand-new feature isn't
 * accidentally hidden before its flag row exists in the DB.
 */
interface FeatureFlagContextValue {
  flags: Record<string, boolean>;
  loaded: boolean;
  refresh: () => Promise<void>;
  isEnabled: (key: string) => boolean;
}

/**
 * Boot defaults for known flags. If `loaded === false` (the first fetch
 * is still in flight), `isEnabled(key)` returns the value from this map
 * (or `true` for unknown keys). Once `loaded === true`, server values
 * are authoritative.
 *
 * Rule of thumb:
 *   - Gated verticals or anything that triggers network calls / reveals
 *     a whole module: default FALSE here.
 *   - Always-on storefront UI: default TRUE.
 */
const LOADING_DEFAULTS: Record<string, boolean> = {
  rides: false,
  food: false,
  stores: true,
  catalog: true,
  deliveries: true,
};

const FeatureFlagContext = createContext<FeatureFlagContextValue>({
  flags: {},
  loaded: false,
  refresh: async () => {},
  isEnabled: () => true,
});

export const FeatureFlagProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [flags, setFlags] = useState<Record<string, boolean>>({});
  const [loaded, setLoaded] = useState(false);

  const refresh = useCallback(async () => {
    try {
      const data = await featureFlagsAPI.getPublicFlags();
      setFlags(data || {});
    } catch (err) {
      // On error, leave existing flags as-is and fail-open: nothing is hidden.
      console.warn('Failed to load feature flags; defaulting all to enabled.', err);
    } finally {
      setLoaded(true);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const isEnabled = useCallback(
    (key: string): boolean => {
      if (!loaded) {
        // Use the per-key boot default so gated verticals don't flash on.
        return LOADING_DEFAULTS[key] ?? true;
      }
      const v = flags[key];
      return v === undefined ? true : v;
    },
    [flags, loaded],
  );

  return (
    <FeatureFlagContext.Provider value={{ flags, loaded, refresh, isEnabled }}>
      {children}
    </FeatureFlagContext.Provider>
  );
};

export const useFeatureFlags = () => useContext(FeatureFlagContext);

export const useFeatureFlag = (key: string): boolean => {
  const { isEnabled } = useContext(FeatureFlagContext);
  return isEnabled(key);
};

/**
 * <FeatureGate flag="rides"> ... </FeatureGate>
 * Renders children only when the flag is enabled.
 */
export const FeatureGate: React.FC<{ flag: string; children: React.ReactNode; fallback?: React.ReactNode }> = ({
  flag,
  children,
  fallback = null,
}) => {
  const enabled = useFeatureFlag(flag);
  return <>{enabled ? children : fallback}</>;
};
